import React, { createContext, useContext, useEffect, useState } from 'react';
import keycloak, { keycloakInitOptions } from '../keycloak-config';

// Keycloak 컨텍스트 생성
const KeycloakContext = createContext();

// Keycloak 컨텍스트 훅
export const useKeycloak = () => {
  const context = useContext(KeycloakContext);
  if (!context) {
    throw new Error('useKeycloak must be used within a KeycloakProvider');
  }
  return context;
};

// Keycloak Provider 컴포넌트
export const KeycloakProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [tokenRefreshAttempts, setTokenRefreshAttempts] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const initKeycloak = async () => {
      try {
        console.log('=== Keycloak 초기화 시작 ===');
        console.log('Keycloak config:', {
          url: keycloak.authServerUrl,
          realm: keycloak.realm,
          clientId: keycloak.clientId
        });
        console.log('Init options:', keycloakInitOptions);
        
        // 초기화 타임아웃 설정 (15초)
        const initTimeout = setTimeout(() => {
          console.error('Keycloak 초기화 타임아웃 - 강제 로그인 실행');
          keycloak.login({
            redirectUri: window.location.origin + '/callback'
          });
        }, 15000);
        
        // Keycloak 초기화
        const authenticated = await keycloak.init(keycloakInitOptions);
        
        // 타임아웃 클리어
        clearTimeout(initTimeout);
        
        console.log('=== Keycloak 초기화 완료 ===');
        console.log('Authenticated:', authenticated);
        console.log('Token exists:', !!keycloak.token);
        console.log('Token expired:', keycloak.isTokenExpired?.());
        console.log('Token parsed:', !!keycloak.tokenParsed);
        
        // 매우 엄격한 인증 검증
        const hasValidToken = keycloak.token && 
                             keycloak.tokenParsed && 
                             !keycloak.isTokenExpired();
        
        const isReallyAuthenticated = authenticated && hasValidToken;
        
        console.log('=== 인증 상태 검증 ===');
        console.log('Has valid token:', hasValidToken);
        console.log('Final authentication status:', isReallyAuthenticated);
        
        if (isReallyAuthenticated) {
          console.log('✅ 인증 성공 - 사용자 정보 설정');
          setIsAuthenticated(true);
          
          // 사용자 정보 추출
          const userInfo = {
            id: keycloak.subject,
            username: keycloak.tokenParsed.preferred_username,
            email: keycloak.tokenParsed.email,
            name: keycloak.tokenParsed.name || keycloak.tokenParsed.preferred_username,
            firstName: keycloak.tokenParsed.given_name,
            lastName: keycloak.tokenParsed.family_name,
            roles: keycloak.tokenParsed.realm_access?.roles || [],
            groups: keycloak.tokenParsed.groups || []
          };
          
          console.log('User info:', userInfo);
          setUser(userInfo);
          
          // 자동 기능 비활성화
          keycloak.onTokenExpired = null;
          keycloak.onAuthSuccess = null;
          keycloak.onAuthError = null;
          keycloak.onAuthRefreshSuccess = null;
          keycloak.onAuthRefreshError = null;
          keycloak.onAuthLogout = null;
          keycloak.onReady = null;
          keycloak.onInitError = null;
          
        } else {
          console.log('❌ 인증 실패 - 로그인 페이지로 리다이렉트');
          setIsAuthenticated(false);
          setUser(null);
          
          // 즉시 로그인 페이지로 리다이렉트 (지연 없음)
          console.log('Redirecting to login...');
          keycloak.login({
            redirectUri: window.location.origin + '/callback'
          });
        }
        
      } catch (initError) {
        console.error('=== Keycloak 초기화 오류 ===', initError);
        
        // 오류 발생 시에도 로그인 페이지로 리다이렉트
        if (initError.message && initError.message.includes('Web Crypto API')) {
          setError('HTTP 환경에서는 보안 제한으로 인해 일부 기능이 제한됩니다. HTTPS 환경을 사용하거나 관리자에게 문의하세요.');
        } else {
          console.log('초기화 오류로 인한 로그인 페이지 리다이렉트');
          setTimeout(() => {
            keycloak.login({
              redirectUri: window.location.origin + '/callback'
            });
          }, 2000);
        }
      } finally {
        setIsLoading(false);
      }
    };

    initKeycloak();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 로그인 함수
  const login = () => {
    keycloak.login();
  };

  // 로그아웃 함수
  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    keycloak.logout({
      redirectUri: window.location.origin
    });
  };

  // 토큰 갱신 함수
  const refreshToken = async () => {
    if (isRefreshing) {
      console.log('Token refresh already in progress');
      return keycloak.token;
    }
    
    try {
      setIsRefreshing(true);
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        console.log('Token refreshed');
        setTokenRefreshAttempts(0); // 성공 시 재시도 카운트 리셋
        return keycloak.token;
      }
      return keycloak.token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      const currentAttempts = tokenRefreshAttempts + 1;
      setTokenRefreshAttempts(currentAttempts);
      
      if (currentAttempts >= 3) {
        console.log('Max token refresh attempts reached, logging out');
        logout();
      }
      return null;
    } finally {
      setIsRefreshing(false);
    }
  };

  // 권한 체크 함수
  const hasRole = (role) => {
    return keycloak.hasRealmRole(role);
  };

  // 리소스 접근 권한 체크 함수
  const hasResourceRole = (resource, role) => {
    return keycloak.hasResourceRole(role, resource);
  };

  // 컨텍스트 값
  const contextValue = {
    keycloak,
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    refreshToken,
    hasRole,
    hasResourceRole,
    token: keycloak.token,
    refreshTokenValue: keycloak.refreshToken
  };

  return (
    <KeycloakContext.Provider value={contextValue}>
      {children}
    </KeycloakContext.Provider>
  );
};

export default KeycloakContext;
